import {
    PersonalDetailMifirExpirationDateSelector, PersonalDetailMifirNumberMaxLengthValidation,
    PersonalDetailMifirNumberSelector, PersonalDetailMifirTypeSelector,
    PersonalDetailOldClientIdMaxLengthValidation,
    PersonalDetailOldClientIdSelector,
    PersonalDetailReferralMaxLengthValidation,
    PersonalDetailReferralSelector, PersonalDetailSecondaryEmailSelector
} from './../../../../../assets/descriptors/panel-clients/personal-details-component.descriptor';
import {FormControl, FormGroup} from '@angular/forms';

import {BehaviorSubject, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    CountryModel,
    EnrollChannelsEnum,
    HttpStatusCodesEnum,
    IndividualsService,
    IndividualInterface,
    LookupModel,
    IndividualModel,
    IndividualInputModel,
    IndividualExtendedModel,
    IndividualPersonnelDetailsInterface,
    IndividualPersonnelDetailsModel,
} from '@spa/api';

import {InArrayValidator, MaxLengthValidatorKey} from '@spa/validators';

import {BaseControl, CountryAutocompleteControl, Helper, SelectControl} from '../../../../shared/classes';

import {
    PersonalDetailsControls,
    PersonalDetailGenderSelector,
    PersonalDetailLastNameSelector,
    PersonalDetailFirstNameSelector,
    PersonalDetailMiddleNameSelector,
    PersonalDetailDateOfBirthSelector,
    PersonalDetailMiFidCategorySelector,
    PersonalDetailCountryOfBirthSelector,
    PersonalDetailTradingCategorySelector,
    PersonalDetailPlaceOfBirth
} from '../../../../../assets/descriptors/panel-clients';

import {PersonalDetailsFormErrorStatesEnum} from '../../enums';
import { EventEmitter } from '@angular/core';
import {PanelClientsEditMessages} from 'appAssets/messages';
import {PatternValidatorKey} from 'appEntries/panel/validators';

export class PersonalDetailsForm {
    private readonly individual: IndividualExtendedModel;
    private readonly countries: CountryModel[];
    private readonly genders: LookupModel;
    private readonly individualsService: IndividualsService;

    public form: FormGroup;
    public controls: BaseControl[];
    public controlsObservable: BehaviorSubject<BaseControl[]>;
    public oldClientIdUpdatedEvent: EventEmitter<string>;

    constructor(
        individual: IndividualExtendedModel,
        countries: CountryModel[],
        genders: LookupModel,
        individualService: IndividualsService
    ) {
        this.individual = individual;
        this.countries = countries;
        this.genders = genders;
        this.individualsService = individualService;
        this.oldClientIdUpdatedEvent = new EventEmitter<string>();

        this.controlsObservable = new BehaviorSubject([]);
    }

    private personalDetailsControlsMap(
        skipGenderControl: boolean,
        skipTradingCategoryControl: boolean,
        skipSecondaryEmailControl: boolean,
        model?: IndividualExtendedModel
    ): BaseControl[] {
        let controls = PersonalDetailsControls();

        if (skipGenderControl) {
            controls = controls.filter((control: BaseControl) => control.name !== PersonalDetailGenderSelector);
        }

        if (skipTradingCategoryControl) {
            controls = controls.filter((control: BaseControl) => control.name !== PersonalDetailTradingCategorySelector);
        }

        if (skipSecondaryEmailControl) {
            controls = controls.filter((control: BaseControl) => control.name !== PersonalDetailSecondaryEmailSelector);
        }

        return controls.map((control: BaseControl) => {
            if (model) {
                control.value = this.getProperty(model, control.name);
            }

            switch (control.name) {
                case PersonalDetailCountryOfBirthSelector:
                    const countryIds = !this.countries ? [] : this.countries.map((country: CountryModel) => country.id);

                    (control as CountryAutocompleteControl).countries = this.countries;

                    control.extendValidators(InArrayValidator(countryIds));
                    break;
                case PersonalDetailGenderSelector:
                    (control as SelectControl).options = this.genders.getTypeKeys().map((key: string) => {
                        return {
                            label: this.genders.getTypeValue(key),
                            value: key
                        };
                    });
                    break;
            }

            return control;
        });
    }

    private getProperty(model: IndividualExtendedModel, controlName: string): string | number {
        switch (controlName) {
            case PersonalDetailFirstNameSelector:
                return model.firstName;
            case PersonalDetailMiddleNameSelector:
                return model.middleName;
            case PersonalDetailLastNameSelector:
                return model.lastName;
            case PersonalDetailGenderSelector:
                return model.gender.key;
            case PersonalDetailDateOfBirthSelector:
                return model.birthDate;
            case PersonalDetailCountryOfBirthSelector:
                return model.birthPlaceId;
            case PersonalDetailPlaceOfBirth:
                return model.birthPlace;
            case PersonalDetailMiFidCategorySelector:
                return model.miFidCategory;
            case PersonalDetailReferralSelector:
                return model.referral_code;
            case PersonalDetailOldClientIdSelector:
                return model.old_client_id;
            case PersonalDetailMifirExpirationDateSelector:
                return model.mifirExpirationDate;
            case PersonalDetailMifirNumberSelector:
                return model.mifirNumber;
            case PersonalDetailMifirTypeSelector:
                return model.mifirType;
            case PersonalDetailSecondaryEmailSelector:
                return model.secondaryEmail;

        }
    }

    private getIndividualMap(
        response: ApiResponse<ApiError[] | IndividualInterface>
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponseObservable<IndividualInterface>(
                response,
                PersonalDetailsFormErrorStatesEnum.GetIndividualFailed
            );
        }

        const data = response.data as IndividualPersonnelDetailsInterface;
        const model = new IndividualPersonnelDetailsModel(data);

        model.firstName = this.form.value[PersonalDetailFirstNameSelector];
        model.middleName = this.form.value[PersonalDetailMiddleNameSelector];
        model.lastName = this.form.value[PersonalDetailLastNameSelector];
        model.gender = this.form.value[PersonalDetailGenderSelector];
        model.birthDate = this.form.value[PersonalDetailDateOfBirthSelector];
        model.birthPlaceId = this.form.value[PersonalDetailCountryOfBirthSelector];
        model.miFidCategory = this.form.value[PersonalDetailMiFidCategorySelector];
        model.birthPlaceLocation = this.form.value[PersonalDetailPlaceOfBirth];
        model.referral_code = this.form.value[PersonalDetailReferralSelector];
        model.old_client_id = this.form.value[PersonalDetailOldClientIdSelector];
        model.mifirNumber = this.form.value[PersonalDetailMifirNumberSelector];
        model.mifirType = this.form.value[PersonalDetailMifirTypeSelector];
        model.mifirExpirationDate = this.form.value[PersonalDetailMifirExpirationDateSelector];
        model.secondaryEmail = Helper.getTrimmedText(this.form.value[PersonalDetailSecondaryEmailSelector], true);

        return this.individualsService.updateIndividualPersonnelDetails(data.id, model)
            .pipe(
                map(this.updateIndividualMap.bind(this, model))
            );
    }

    private updateIndividualMap(
        model: IndividualPersonnelDetailsModel,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<null>(
                response,
                PersonalDetailsFormErrorStatesEnum.UpdateIndividualFailed
            );
        }

        this.individual.firstName = model.firstName;
        this.individual.middleName = model.middleName;
        this.individual.lastName = model.lastName;
        this.individual.miFidCategory = model.miFidCategory;
        this.individual.birthPlace = model.birthPlaceLocation;
        this.individual.referral_code = model.referral_code;
        this.individual.mifirNumber = model.mifirNumber;
        this.individual.mifirType = model.mifirType;
        this.individual.mifirExpirationDate = model.mifirExpirationDate;
        this.individual.secondaryEmail = model.secondaryEmail;
        if (this.individual.old_client_id !== model.old_client_id) {
            this.oldClientIdUpdatedEvent.emit(model.old_client_id);
        }
        this.individual.old_client_id = model.old_client_id;

        const genderKeys = this.genders.getTypeKeys().filter((key: string) => {
            return model.gender === key;
        });

        if (genderKeys.length !== 0) {
            this.individual.gender.key = model.gender;
            this.individual.gender.value = this.genders.getTypeValue(genderKeys[0]);
        }

        this.individual.birthDate = model.birthDate;
        this.individual.birthPlaceId = model.birthPlaceId;

        return Helper.createSuccessResponse<boolean>(true);
    }

    private createIndividualInputModel(nationalities): IndividualInputModel {
        return new IndividualInputModel(
            this.individual.userCorrelationId,
            this.form.value[PersonalDetailFirstNameSelector],
            this.form.value[PersonalDetailMiddleNameSelector],
            this.form.value[PersonalDetailLastNameSelector],
            this.form.value[PersonalDetailDateOfBirthSelector],
            this.form.value[PersonalDetailCountryOfBirthSelector],
            this.form.value[PersonalDetailPlaceOfBirth],
            EnrollChannelsEnum.Offline,
            this.form.value[PersonalDetailGenderSelector],
            nationalities,
            Helper.enumsHelper().getDefaultTenantId(),
            this.form.value[PersonalDetailReferralSelector],
            null,
            this.form.value[PersonalDetailOldClientIdSelector],
            this.form.value[PersonalDetailMifirNumberSelector],
            this.form.value[PersonalDetailMifirExpirationDateSelector],
            this.form.value[PersonalDetailMifirTypeSelector]
        );
    }

    initForm(skipGenderControl: boolean = false,
             skipTradingCategoryControl: boolean = false,
             skipSecondaryEmailControl: boolean = false): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (!this.individual) {
            this.controls = this.personalDetailsControlsMap(skipGenderControl,
                skipTradingCategoryControl,
                skipSecondaryEmailControl);
        } else {
            this.controls = this.personalDetailsControlsMap(skipGenderControl,
                skipTradingCategoryControl,
                skipSecondaryEmailControl,
                this.individual);
        }

        this.controls.forEach((control: BaseControl) => {
            this.form.addControl(control.name, new FormControl(control.value, control.validators));
        });

        this.controlsObservable.next(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        if (!this.form.valid) {
            this.controls.map((control: BaseControl) => {
                if (!this.form.controls[control.name]) {
                    return false;
                }

                const formControl = this.form.controls[control.name];

                if (formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((key: string) => {

                        if (key === MaxLengthValidatorKey && control.name === PersonalDetailReferralSelector) {
                            return messageFunction(control.placeholder, PersonalDetailReferralMaxLengthValidation)[key];
                        }
                        if (key === MaxLengthValidatorKey && control.name === PersonalDetailOldClientIdSelector) {
                            return messageFunction(control.placeholder, PersonalDetailOldClientIdMaxLengthValidation)[key];
                        }

                        if (key === MaxLengthValidatorKey && control.name === PersonalDetailMifirNumberSelector) {
                            return messageFunction(control.placeholder, PersonalDetailMifirNumberMaxLengthValidation)[key];
                        }

                        if (key === PatternValidatorKey && control.name === PersonalDetailMifirNumberSelector) {
                            return messageFunction(control.placeholder)[PanelClientsEditMessages.MifirNumberIsWrong.key];
                        }

                        return messageFunction(control.placeholder)[key];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(nationalities?: string[]): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (this.form.dirty) {
            if (this.individual && this.individual.id) {
                return this.individualsService.getById(this.individual.id)
                    .pipe(
                        switchMap(this.getIndividualMap.bind(this))
                    );
            } else {
                const inputModel = this.createIndividualInputModel(nationalities);

                return this.individualsService.register(inputModel)
                    .pipe(
                        switchMap((response: ApiResponse<ApiError[] | IndividualInterface>) => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                return Helper.errorsHelper().createErrorResponseObservable<IndividualInterface>(
                                    response,
                                    PersonalDetailsFormErrorStatesEnum.CreateIndividualFailed
                                );
                            }

                            const data = response.data as IndividualInterface;
                            const model = new IndividualModel(data);
                            model.forceAlias = false;
                            this.individual.id = model.id;

                            model.miFidCategory = this.form.value[PersonalDetailMiFidCategorySelector];
                            model.tradingCategory = this.form.value[PersonalDetailTradingCategorySelector];

                            return this.individualsService.update(model).pipe(
                                map((updateResponse: ApiResponse<ApiError[] | null>) => {
                                    if (updateResponse.status !== HttpStatusCodesEnum.Ok) {
                                        return Helper.errorsHelper().createErrorResponse<null>(updateResponse);
                                    }

                                    this.individual.miFidCategory = model.miFidCategory;
                                    this.individual.tradingCategory = model.tradingCategory;

                                    return Helper.createSuccessResponse<boolean>(true);
                                })
                            );
                        })
                    );
            }
        }

        return null;
    }
}
